// Revenue Projection sub-tab — SGNL-213.
//
// Mounted as the third sub-view inside Pipeline Analytics (alongside
// Stage Velocity + Funnel). Renders the probability-weighted forecast:
//
//   projected = Σ (amount × probability/100) for OPEN opps where
//               probability ≥ min (default ≥5%)
//
// Per-stage breakdown table shows how the formula deflates each stage
// — operators can see at a glance which stage carries the most
// pipeline mass and how much of it survives the weighting.

import {
  computeWeightedRevenueProjection,
  type RevenueProjection,
  type RevenueProjectionStageRow,
} from "../lib/aggregations";
import { esc, fmtMoney } from "../lib/format";
import { applyFilters, getState, subscribe } from "../state";
import {
  createTabbedPreset,
  STANDARD_PROBABILITY_PRESETS,
  type StandardProbabilityPresetValue,
} from "../lib/tabbed_preset";

// ---------------------------------------------------------------------------
// Tab-scoped probability preset (sessionStorage-backed).
// First consumer of the new shared `createTabbedPreset` factory —
// architect MED on the SGNL-214 review surfaced the duplication
// pattern across Progress + Lost. SGNL-213 ships the factory; the
// retrofit of weekly_call.ts / lost.ts is a future ticket.
// ---------------------------------------------------------------------------

const _preset = createTabbedPreset<StandardProbabilityPresetValue>({
  presets: STANDARD_PROBABILITY_PRESETS,
  defaultValue: "q5",
  sessionKey: "signal:revenue-projection-probability-preset",
});

export function getRevenueProjectionPreset(): StandardProbabilityPresetValue {
  return _preset.get();
}

export function setRevenueProjectionPreset(value: string): void {
  _preset.set(value);
}

/** Test-only seam — reset module state between cases. */
export function _resetRevenueProjectionState(): void {
  _preset.resetForTests();
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderFormulaCallout(min: number | null): string {
  const minLabel = min === null ? "all opps" : `probability ≥ ${min}%`;
  // Inline the formula in the card body — operator-facing transparency
  // about how the headline number is computed (acceptance criterion).
  return `
    <p class="muted" style="margin:0 0 10px; font-size:13px;">
      Forecast = <code>Σ (amount × probability / 100)</code> across
      OPEN opps with ${esc(minLabel)}. Closed deals are excluded — they're
      already revenue, not forecast.
    </p>`;
}

function renderProbabilityFilterBar(): string {
  const current = _preset.get();
  return `
    <div class="progress-probability-filter" data-testid="projection-probability-filter">
      <label class="progress-probability-label">
        Min probability
        <select data-testid="projection-probability-select"
                aria-label="Filter the Revenue Projection by minimum deal probability (%)"
                aria-describedby="projection-probability-help">
          ${_preset.presets.map(
            (p) =>
              `<option value="${esc(p.value)}" ${current === p.value ? "selected" : ""}>${esc(p.label)}</option>`,
          ).join("")}
        </select>
      </label>
      <p id="projection-probability-help" class="muted progress-probability-help">
        Default ≥5% drops MQL / Stage-0 deals so the projection
        reflects deals that have at least reached qualification.
      </p>
    </div>`;
}

function renderHeadlineCard(p: RevenueProjection, min: number | null): string {
  // Deflation ratio = how much of the raw open-pipeline survives the
  // probability weighting. Shown alongside the headline so operators
  // see "your $40M open pipeline weights to $12M expected revenue"
  // without needing to do the math.
  const deflation = p.total_raw > 0
    ? `${Math.round((p.headline_total / p.total_raw) * 100)}%`
    : "—";
  // a11y review: `<output>` is the WCAG-friendly idiom for a "live
  // computed value" — screen readers announce it as a result anchor
  // rather than a generic span. The `aria-live="polite"` makes the
  // recomputed forecast announce when the dropdown / filter bar
  // changes the projection.
  return `
    <div class="projection-headline" data-testid="projection-headline">
      <span class="projection-headline-label">Weighted Pipeline Forecast</span>
      <output class="projection-headline-value"
              data-testid="projection-headline-value"
              aria-live="polite">
        ${fmtMoney(p.headline_total)}
      </output>
      <span class="muted projection-headline-meta">
        ${p.contributing_opps.toLocaleString()} open opp${p.contributing_opps === 1 ? "" : "s"}
        · raw ${fmtMoney(p.total_raw)} · ${deflation} of raw
      </span>
      ${renderFormulaCallout(min)}
    </div>`;
}

function renderPerStageTable(rows: RevenueProjectionStageRow[]): string {
  if (rows.length === 0) {
    return `<p class="empty-bucket">No open opps match the current filter.</p>`;
  }
  return `
    <div class="table-wrap">
      <table class="data-table" data-testid="projection-per-stage-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th class="num"># opps</th>
            <th class="num">Raw $</th>
            <th class="num">Weighted $</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `
            <tr data-testid="projection-stage-row" data-stage="${esc(r.stage)}">
              <td>${esc(r.stage)}</td>
              <td class="num">${r.count.toLocaleString()}</td>
              <td class="num">${fmtMoney(r.raw_amount)}</td>
              <td class="num">${fmtMoney(r.weighted_amount)}</td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>`;
}

// ---------------------------------------------------------------------------
// Entry point + subscribe management.
//
// The Pipeline Analytics shell mounts the sub-tab by calling
// renderRevenueProjectionTab(body). We subscribe to the global state
// so global-filter-bar edits (date range / BU / etc.) re-render the
// projection in place. The subscription is per-host: an `unsubscribe`
// function is stashed on the host so re-mounts (sub-tab swap → swap
// back) abort the prior subscription before attaching a fresh one.
// ---------------------------------------------------------------------------

const _hostUnsubs = new WeakMap<HTMLElement, () => void>();

/** Tear down the subscribe listener registered for this host.
 *
 * Architect MED pre-PR review: prior to this export, sub-tab swaps
 * (Projection → Funnel / Velocity) destroyed the body's DOM via
 * `body.innerHTML = ""` but did NOT unhook the subscribe listener,
 * which continued to fire `renderBody` against the detached body on
 * every state notify. Pipeline Analytics' `mountSub` now calls this
 * helper alongside `_abortStageVelocity` / `_abortConversionFunnel`
 * to guarantee clean teardown on every sub-tab swap. */
export function abortRevenueProjection(host: HTMLElement): void {
  _hostUnsubs.get(host)?.();
  _hostUnsubs.delete(host);
}

export function renderRevenueProjectionTab(host: HTMLElement): void {
  // Tear down any prior subscription on the same host (sub-tab swap
  // re-renders this view multiple times). Without this, every re-mount
  // would stack a fresh notify listener and the global-filter-edit
  // path would re-render N times for N prior mounts.
  _hostUnsubs.get(host)?.();

  const renderBody = (): void => {
    // a11y: capture focus state BEFORE the innerHTML wipe so the
    // subscribe-driven re-render (filter-bar edit while the select
    // is focused) doesn't drop focus. Pattern mirrors SGNL-209's
    // captureFocusSnapshot/restoreFocusSnapshot helpers.
    const hadFocus = host.contains(host.ownerDocument.activeElement)
      && (host.ownerDocument.activeElement as HTMLElement | null)?.dataset.testid
        === "projection-probability-select";

    // Capture `min` once — same value flows into both
    // `projectionFromState` and the formula label (code-reviewer MED:
    // previously called `_preset.getMin()` twice per render).
    const min = _preset.getMin();
    const state = getState();
    const filtered = state.data
      ? applyFilters(state.data.opps, state.filters)
      : [];
    const projection = computeWeightedRevenueProjection(filtered, min);
    host.innerHTML = `
      <div class="tab-content" data-testid="revenue-projection-tab">
        ${renderProbabilityFilterBar()}
        ${renderHeadlineCard(projection, min)}
        ${renderPerStageTable(projection.per_stage)}
      </div>`;

    // Probability dropdown — set the preset + re-render. No network
    // round-trip; the projection is a pure function of state + preset.
    const select = host.querySelector<HTMLSelectElement>(
      '[data-testid="projection-probability-select"]',
    );
    select?.addEventListener("change", () => {
      _preset.set(select.value);
      renderBody();
    });

    // Restore focus to the freshly rendered select if it had focus
    // before the wipe — covers BOTH the dropdown's own onChange path
    // AND the subscribe-driven re-render path.
    if (hadFocus) {
      host
        .querySelector<HTMLSelectElement>('[data-testid="projection-probability-select"]')
        ?.focus();
    }
  };

  renderBody();

  const unsubscribe = subscribe(() => {
    // PR-Agent #132 (imp 6): self-teardown if the host is detached
    // from the DOM. Covers the full-tab unmount path (user navigates
    // away from #pipeline_analytics entirely) which doesn't pass
    // through mountSub's `abortRevenueProjection` cleanup. Without
    // this guard the subscribe listener would leak + write into a
    // dead DOM node on every state notify until page reload.
    if (!host.isConnected) {
      abortRevenueProjection(host);
      return;
    }
    // Filter-bar edits land in state via setFilters → subscribe fires;
    // re-render the projection in place. We skip re-render when the
    // active tab is NOT pipeline_analytics (the user has moved away
    // but the host is still in the DOM tree because main.ts may
    // retain it). The teardown via `abortRevenueProjection` happens
    // on the actual sub-tab swap; this is the cheap cross-tab thrash
    // guard for the still-attached-but-not-active case.
    if (getState().activeTab !== "pipeline_analytics") return;
    renderBody();
  });
  _hostUnsubs.set(host, unsubscribe);
}
